import { ROLES, type Role } from "@wcp/shared";
import { useState } from "react";
import { useBusiness } from "../../lib/business.js";
import { useMe } from "../auth/api.js";
import {
  type Connection,
  useConnection,
  useCreateMember,
  useDemoData,
  useIntegrations,
  useSetDemoData,
  useSyncConnection,
  useTeam,
  useUpdateMember,
} from "./api.js";
import { EmbeddedSignup } from "./EmbeddedSignup.js";
import styles from "./SettingsScreen.module.css";

type Tab = "connection" | "integrations" | "team" | "system";

const BASE_TABS: Array<[Tab, string]> = [
  ["connection", "Connection"],
  ["integrations", "Integrations"],
  ["team", "Team"],
];

// Inline check glyph (ports prototype I.check, hillman-demo.css — must be sized
// by .optin svg or it balloons). 14×14 viewBox.
function CheckIcon() {
  return (
    <svg viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <path
        d="M2.5 7.5l3 3 6-7"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// Generic person silhouette (prototype I.personFill) — the product uses a glyph,
// never initials text.
function PersonGlyph() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="8.8" r="4" fill="currentColor" />
      <path d="M3.6 21.5c0-4.7 3.7-7.3 8.4-7.3s8.4 2.6 8.4 7.3z" fill="currentColor" />
    </svg>
  );
}

export function SettingsScreen() {
  const [tab, setTab] = useState<Tab>("connection");
  const me = useMe();
  const isAdmin = me.data?.role === "admin";
  // The System tab (demo-data toggle) is admin-only.
  const tabs: Array<[Tab, string]> = isAdmin ? [...BASE_TABS, ["system", "System"]] : BASE_TABS;

  return (
    <section className={styles.wrap}>
      <header className={styles.head}>
        <h1 className={styles.title}>Settings</h1>
        <p className={styles.sub}>Connection · integrations · team</p>
      </header>

      <div className={styles.tabs} role="tablist" aria-label="Settings sections">
        {tabs.map(([k, lbl]) => (
          <button
            key={k}
            type="button"
            role="tab"
            aria-selected={tab === k}
            className={`${styles.tab} ${tab === k ? styles.tabOn : ""}`}
            onClick={() => setTab(k)}
          >
            {lbl}
          </button>
        ))}
      </div>

      <div className={styles.pane}>
        {tab === "connection" && <ConnectionPanel isAdmin={isAdmin} />}
        {tab === "integrations" && <IntegrationsPanel />}
        {tab === "team" && <TeamPanel isAdmin={isAdmin} />}
        {tab === "system" && isAdmin && <SystemPanel />}
      </div>
    </section>
  );
}

// Admin-only developer tools (doc 16). Demo data toggle: load/clear realistic sample
// data so the populated screens can be explored; disabled in production (server-gated).
function SystemPanel() {
  const demo = useDemoData();
  const setDemo = useSetDemoData();
  const loaded = demo.data?.loaded ?? false;
  const available = demo.data?.available ?? false;
  const busy = demo.isLoading || setDemo.isPending;

  return (
    <div className={styles.panel}>
      <div className={styles.panelHead}>
        <span className={styles.panelTitle}>Demo data</span>
        <span className={styles.panelMeta}>staging / testing</span>
      </div>
      <div className={styles.demoRow}>
        <div className={styles.demoText}>
          <span className={styles.demoLabel}>Load sample data</span>
          <span className={styles.demoDesc}>
            Populates The Pisau with realistic contacts, conversations, broadcasts, campaigns and
            orders so you can explore the screens with data. Toggling off clears it.
            {!available && " Disabled in production."}
          </span>
          {setDemo.isError && (
            <span className={styles.demoErr}>Could not update demo data. Try again.</span>
          )}
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={loaded}
          aria-label="Load demo data"
          className={`${styles.toggle} ${loaded ? styles.toggleOn : ""}`}
          disabled={busy || !available}
          onClick={() => setDemo.mutate(!loaded)}
        >
          <span className={styles.toggleThumb} />
        </button>
      </div>
    </div>
  );
}

const QUALITY_LABEL: Record<Connection["quality"], string> = {
  high: "High",
  medium: "Medium",
  low: "Low",
  unknown: "Not rated yet",
};

function ConnectionPanel({ isAdmin }: { isAdmin: boolean }) {
  const { activeId } = useBusiness();
  const conn = useConnection(activeId);
  const sync = useSyncConnection(activeId);

  if (conn.isLoading) {
    return (
      <div className={styles.panel}>
        <div className={styles.panelHead}>
          <span className={styles.panelTitle}>WhatsApp connection</span>
          <span className={styles.panelMeta}>official Cloud API</span>
        </div>
        <p className={styles.loading}>Loading…</p>
      </div>
    );
  }
  if (!conn.data) {
    return (
      <div className={styles.panel}>
        <div className={styles.panelHead}>
          <span className={styles.panelTitle}>WhatsApp connection</span>
          <span className={styles.panelMeta}>official Cloud API</span>
        </div>
        <p className={styles.loading}>No connection data.</p>
      </div>
    );
  }

  const c = conn.data;
  // High quality lights up the teal "optin yes" badge; everything else is plain
  // mist-dim text — the expected no-Meta-data staging empty state.
  const qualityHigh = c.quality === "high";

  return (
    <div className={styles.panel}>
      <div className={styles.panelHead}>
        <span className={styles.panelTitle}>WhatsApp connection</span>
        <span className={styles.panelMeta}>
          official Cloud API
          {isAdmin && (
            <button
              type="button"
              className="btn"
              style={{ marginLeft: 10, padding: "4px 10px", fontSize: 12 }}
              disabled={sync.isPending}
              title="Pull quality, tier and verification from the Graph API"
              onClick={() => sync.mutate()}
            >
              {sync.isPending ? "Syncing…" : "Sync from Meta"}
            </button>
          )}
        </span>
      </div>
      {sync.isError && (
        <p className={styles.loading}>
          Sync unavailable: {sync.error instanceof Error ? sync.error.message : "no live Meta"}
        </p>
      )}
      <dl className={styles.kv}>
        <div>
          <dt>Display name</dt>
          <dd className={styles.mono}>{c.name}</dd>
        </div>
        <div>
          <dt>Business number</dt>
          <dd className={styles.mono}>{c.phoneDisplay ?? "—"}</dd>
        </div>
        <div>
          <dt>WhatsApp Business Account</dt>
          <dd className={styles.mono}>{c.wabaId ?? "—"}</dd>
        </div>
        <div>
          <dt>Quality rating</dt>
          <dd>
            {qualityHigh ? (
              <span className={`${styles.optin} ${styles.optinYes}`}>
                {QUALITY_LABEL[c.quality]}
              </span>
            ) : (
              <span className={styles.optinPlain}>{QUALITY_LABEL[c.quality]}</span>
            )}
          </dd>
        </div>
        <div>
          <dt>Messaging tier</dt>
          <dd className={styles.mono}>{c.tier ? `${c.tier.toLocaleString()} / day` : "—"}</dd>
        </div>
        <div>
          <dt>Status</dt>
          <dd>
            {c.connected ? (
              <span className={`${styles.optin} ${styles.optinYes}`}>
                <CheckIcon /> Connected
              </span>
            ) : (
              <span className={styles.optinPlain}>Setup pending</span>
            )}
          </dd>
        </div>
      </dl>
      {!c.connected && isAdmin && (
        <EmbeddedSignup businessId={activeId} wabaSlug={c.wabaSlug} numberSlug={c.numberSlug} />
      )}
    </div>
  );
}

const PLATFORM_LABEL: Record<string, string> = {
  shopify: "Shopify",
  woocommerce: "WooCommerce",
};
const capPlatform = (p: string) =>
  PLATFORM_LABEL[p.toLowerCase()] ?? p.charAt(0).toUpperCase() + p.slice(1);

function IntegrationsPanel() {
  const { activeId } = useBusiness();
  const integ = useIntegrations(activeId);
  const stores = integ.data ?? [];

  return (
    <div className={styles.panel}>
      <div className={styles.panelHead}>
        <span className={styles.panelTitle}>Store integrations</span>
        <span className={styles.panelMeta}>order webhooks</span>
      </div>
      <div className={styles.integ}>
        {integ.isLoading && <div className={styles.integEmpty}>Loading…</div>}
        {!integ.isLoading &&
          stores.map((s) => (
            <div key={s.id} className={styles.integRow}>
              <span className={styles.integName}>
                {capPlatform(s.platform)} · {s.domain}
              </span>
              {s.status === "connected" ? (
                <span className={`${styles.optin} ${styles.optinYes}`}>
                  <CheckIcon /> Connected
                </span>
              ) : (
                <span className="chip" data-status={s.status}>
                  {s.status}
                </span>
              )}
            </div>
          ))}
        {!integ.isLoading && stores.length === 0 && (
          <div className={styles.integEmpty}>No stores for this brand.</div>
        )}
      </div>
    </div>
  );
}

const ROLE_KIND: Record<string, string> = {
  admin: "info",
  agent: "muted",
  maintainer: "ok",
};
const ROLE_LEGEND: Array<[string, string, string]> = [
  ["admin", "Admin", "Full control — billing, templates, team, all screens."],
  ["agent", "Agent", "Inbox & contact handling only."],
  ["maintainer", "Maintainer", "Agency upkeep — API, automations, quality."],
];
const ROLE_NOTE: Record<string, string> = Object.fromEntries(
  ROLE_LEGEND.map(([key, , note]) => [key, note]),
);

function TeamPanel({ isAdmin }: { isAdmin: boolean }) {
  const team = useTeam();
  const create = useCreateMember();
  const update = useUpdateMember();
  const [form, setForm] = useState({ name: "", email: "", role: "agent" as Role, password: "" });
  const members = team.data ?? [];

  return (
    <div className={styles.stack}>
      <div className={styles.panel}>
        <div className={styles.panelHead}>
          <span className={styles.panelTitle}>Team</span>
          <span className={styles.panelMeta}>
            {members.length} member{members.length === 1 ? "" : "s"}
          </span>
        </div>
        <div className={styles.team}>
          {team.isLoading && <div className={styles.integEmpty}>Loading…</div>}
          {!team.isLoading &&
            members.map((m) => {
              const kind = ROLE_KIND[m.role] ?? "muted";
              const note = ROLE_NOTE[m.role] ?? "";
              return (
                <div key={m.id} className={styles.teamRow}>
                  <span className={`${styles.av} ${styles.avGeneric}`} aria-hidden="true">
                    <PersonGlyph />
                  </span>
                  <span className={styles.teamName}>{m.name}</span>
                  {isAdmin ? (
                    <>
                      <select
                        className={styles.roleSelect}
                        aria-label={`Role for ${m.name}`}
                        value={m.role}
                        onChange={(e) => update.mutate({ id: m.id, role: e.target.value as Role })}
                      >
                        {ROLES.map((r) => (
                          <option key={r} value={r}>
                            {r}
                          </option>
                        ))}
                      </select>
                      {m.isActive ? (
                        <button
                          type="button"
                          className={styles.linkBtn}
                          onClick={() => update.mutate({ id: m.id, isActive: false })}
                        >
                          disable
                        </button>
                      ) : (
                        <span className={`${styles.tagpill} ${styles.muted}`}>disabled</span>
                      )}
                    </>
                  ) : (
                    <span
                      className={`${styles.teamRole} ${styles.tagpill} ${styles[kind] ?? styles.muted}`}
                      title={note}
                    >
                      {m.role}
                    </span>
                  )}
                </div>
              );
            })}
        </div>
        <div className={styles.teamLegend}>
          {ROLE_LEGEND.map(([key, label, note]) => (
            <div key={key} className={styles.tlgRow}>
              <b>{label}</b>
              <span>{note}</span>
            </div>
          ))}
        </div>
      </div>

      {isAdmin && (
        <form
          className={styles.panel}
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate(form, {
              onSuccess: () => setForm({ name: "", email: "", role: "agent", password: "" }),
            });
          }}
        >
          <div className={styles.panelHead}>
            <span className={styles.panelTitle}>Add member</span>
          </div>
          <div className={styles.formBody}>
            <div className={styles.formRow}>
              <input
                placeholder="Name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />
              <input
                type="email"
                placeholder="Email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
              />
              <select
                aria-label="New member role"
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value as Role })}
              >
                {ROLES.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
              <input
                type="password"
                placeholder="Initial password (≥8)"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                minLength={8}
                required
              />
              <button className="btn btn--primary" type="submit" disabled={create.isPending}>
                Add
              </button>
            </div>
            {create.isError && (
              <p className={styles.err}>Could not add member (duplicate email?).</p>
            )}
          </div>
        </form>
      )}
    </div>
  );
}
